/**
 * Le vocabulaire de la comparaison (V1.6-07).
 *
 * C'est ici que se joue la règle de l'écran : **ne jamais afficher un chiffre
 * qu'on n'a pas.** Un total inconnu s'écrit « inconnu » et se dit pourquoi ; il
 * ne s'écrit pas « 0,00 $ », parce qu'un acheteur qui lit zéro conclut gratuit.
 *
 * Le mot « meilleur » n'apparaît nulle part, et ce n'est pas une pudeur de
 * langage : il n'y a pas de meilleur fournisseur dans l'absolu. Il y a le moins
 * cher, le plus rapide, le plus simple, et un compromis — chacun avec ce qu'il
 * ignore.
 */
import type {
  ComparisonWarning,
  Coverage,
  ScenarioKind,
  ScenarioStatus,
} from "@/api/types";
import type { BadgeTone } from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";

/**
 * Un montant **dans sa devise**, ou un tiret.
 *
 * `formatMoney` de `format.ts` suppose le dollar canadien, ce qui est juste
 * partout ailleurs dans le produit. Ici c'est faux : une soumission américaine
 * affichée « 900,00 $ CA » serait un mensonge de neuf cents dollars, et
 * V1.6-07 ne convertit aucune devise. La devise s'écrit donc telle que le
 * fournisseur l'a donnée.
 */
export function formatAmount(
  amount: string | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount === null || amount === undefined) return "—";
  const value = Number(amount);
  if (Number.isNaN(value)) return "—";
  const formatted = new Intl.NumberFormat("fr-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  return currency ? `${formatted} ${currency}` : `${formatted} (devise inconnue)`;
}

/**
 * Un coût total, ou la raison de son absence.
 *
 * Séparé de `formatAmount` parce que l'absence dit ici quelque chose de
 * précis : ce n'est pas « pas encore saisi », c'est « on ne peut pas le
 * calculer, voici pourquoi ». Et la raison dépend de ce que le moteur a pu
 * faire — d'où le statut plutôt qu'une liste de noms.
 */
export function formatTotal(
  amount: string | null,
  currency: string | null,
  status: ScenarioStatus,
): string {
  if (amount !== null) return formatAmount(amount, currency);
  switch (status) {
    case "INCONCLUSIVE":
      return "Aucune combinaison entièrement chiffrée";
    case "LIMIT_EXCEEDED":
      return "Trop de soumissions pour garantir l'optimum";
    default:
      return "Inconnu — plusieurs devises, aucune conversion";
  }
}

/**
 * Ce qu'un scénario garantit, en clair.
 *
 * Un écran qui affiche « le moins cher » doit pouvoir dire si c'en est
 * vraiment un. La première version ne le pouvait pas — et se trompait.
 */
export function formatScenarioStatus(status: ScenarioStatus): string {
  switch (status) {
    case "EXACT":
      return "Optimum prouvé";
    case "HEURISTIC":
      return "Compromis";
    case "INCONCLUSIVE":
      return "Sans conclusion";
    case "LIMIT_EXCEEDED":
      return "Hors borne de calcul";
    default:
      return status;
  }
}

export function scenarioStatusTone(status: ScenarioStatus): BadgeTone {
  switch (status) {
    case "EXACT":
      return "available";
    case "HEURISTIC":
      return "info";
    case "INCONCLUSIVE":
      return "warning";
    case "LIMIT_EXCEEDED":
      return "out";
    default:
      return "unknown";
  }
}

/** Un délai annoncé. L'absence n'est pas « tout de suite ». */
export function formatLeadTime(days: number | null): string {
  if (days === null) return "Délai inconnu";
  if (days === 0) return "Disponible immédiatement";
  return days === 1 ? "1 jour" : `${days} jours`;
}

/** Ce que le fournisseur disait avoir, **et quand il le disait**. */
export function formatAssertedAvailability(
  quantity: string | null,
  assertedOn: string | null,
): string {
  if (quantity === null) return "Disponibilité non dite";
  const amount = Number(quantity);
  const stock = Number.isNaN(amount) ? quantity : String(amount);
  if (!assertedOn) return `${stock} (date inconnue)`;
  return `${stock} selon la soumission du ${formatDate(assertedOn)}`;
}

export function formatScenarioKind(kind: ScenarioKind): string {
  switch (kind) {
    case "LOWEST_KNOWN_COST":
      return "Le moins cher";
    case "FEWEST_SUPPLIERS":
      return "Le moins de fournisseurs";
    case "FASTEST_KNOWN":
      return "Le plus rapide";
    case "BALANCED":
      return "Compromis";
    default:
      return kind;
  }
}

export function formatCoverage(coverage: Coverage): string {
  switch (coverage) {
    case "FULLY_COVERED":
      return "Tout est couvert";
    case "PARTIALLY_COVERED":
      return "Couvert en partie";
    case "NOT_COVERED":
      return "Rien de couvert";
    case "INCOMPARABLE":
      return "Pas comparable";
    default:
      return coverage;
  }
}

export function coverageTone(coverage: Coverage): BadgeTone {
  switch (coverage) {
    case "FULLY_COVERED":
      return "available";
    case "PARTIALLY_COVERED":
      return "info";
    case "NOT_COVERED":
      return "out";
    case "INCOMPARABLE":
      return "unknown";
    default:
      return "unknown";
  }
}

/**
 * Ce qu'un avertissement veut dire, en français d'atelier.
 *
 * Chacun est écrit comme une **action à mener** quand il en appelle une :
 * « rappeler le fournisseur » se fait, « NEEDS_CLARIFICATION » ne se fait pas.
 */
export function formatComparisonWarning(warning: ComparisonWarning): string {
  switch (warning) {
    case "NOT_QUOTED":
      return "Pas de réponse sur cette ligne";
    case "UNAVAILABLE":
      return "Annoncé indisponible";
    case "DISCONTINUED":
      return "Produit discontinué";
    case "NEEDS_CLARIFICATION":
      return "Réponse illisible — rappeler le fournisseur";
    case "ALTERNATIVE_REQUIRES_APPROVAL":
      return "Substitut proposé — équivalence à approuver au catalogue";
    case "NO_PRICE":
      return "Aucun prix — rien à comparer";
    case "INCOMPARABLE_UNIT":
      return "Unité non convertible vers celle demandée";
    case "UNIT_UNKNOWN":
      return "Unité non précisée — demander laquelle";
    case "CURRENCY_MISSING":
      return "Devise non précisée";
    case "CURRENCY_MISMATCH":
      return "Devises différentes — aucune conversion n'est faite";
    case "PACKAGING_UNKNOWN":
      return "Conditionnement annoncé sans sa taille";
    case "PARTIAL_AVAILABILITY":
      return "Quantité insuffisante chez ce fournisseur";
    case "AVAILABILITY_UNKNOWN":
      return "Disponibilité non dite";
    case "LEAD_TIME_UNKNOWN":
      return "Délai non dit";
    case "FREIGHT_UNKNOWN":
      return "Transport non chiffré — le coût total reste inconnu";
    case "SURPLUS":
      return "Oblige à acheter plus que le besoin";
    case "QUOTE_EXPIRED":
      return "Soumission périmée d'après sa date de validité";
    case "NO_OFFER":
      return "Aucune soumission validée pour cette ligne";
    default:
      return warning;
  }
}

/** Un avertissement bloquant se voit ; les autres s'informent. */
export function warningTone(warning: ComparisonWarning, blocking: boolean): BadgeTone {
  if (blocking) return "out";
  return warning === "SURPLUS" || warning === "QUOTE_EXPIRED" ? "info" : "unknown";
}
